import React from "react";

/**
 * Catches render/commit-phase errors from anything below it so a single broken
 * component degrades to an inline message instead of unmounting the whole app
 * (React unmounts the entire tree when a commit-phase error reaches the root).
 *
 * `resetKey` — change it (e.g. to the current route or creative_id) to clear a
 * previous error and retry rendering the children.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, componentStack: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the component stack — it names the component that actually threw,
    // which is what we need to diagnose crashes reported from UAT/live.
    this.setState({ componentStack: info?.componentStack || null });
    console.error(`[ErrorBoundary${this.props.label ? `: ${this.props.label}` : ""}]`, error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null, componentStack: null });
    }
  }

  handleRetry = () => this.setState({ error: null, componentStack: null });

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    if (typeof this.props.fallback === "function") {
      return this.props.fallback({ error, retry: this.handleRetry });
    }
    if (this.props.fallback) return this.props.fallback;

    return (
      <div className="flex h-full w-full items-center justify-center p-4">
        <div className="w-full max-w-md rounded-xl border border-red-200 bg-red-50 p-4 sm:p-6 text-center">
          <h2 className="text-base sm:text-lg font-semibold text-red-800">
            {this.props.title || "Something went wrong"}
          </h2>
          <p className="mt-1 text-xs sm:text-sm text-red-700 break-words">
            {error?.message || "An unexpected error occurred."}
          </p>

          <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-center">
            <button
              onClick={this.handleRetry}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-100"
            >
              Reload page
            </button>
          </div>

          {componentStack && (
            <details className="mt-3 text-left">
              <summary className="cursor-pointer text-[11px] text-red-500">Technical details</summary>
              <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-white/70 p-2 text-[10px] leading-tight text-gray-600">
                {String(error?.stack || error)}
                {componentStack}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
